import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { ALL_CATEGORIES, CATEGORY_META } from "@/data/categories";
import { ListingCard } from "@/components/ListingCard";
import { ServiceCard } from "@/components/ServiceCard";
import { LocationPicker, readCity } from "@/components/LocationPicker";
import { VerifiedSlider } from "@/components/VerifiedSlider";
import { EUROPE_COUNTRIES, citiesFor, CITY_GROUPS } from "@/data/europe";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Browse() {
  const { t, tc } = useLang();
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [meta, setMeta] = useState({ countries: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "all";
  const country = params.get("country") || "all";
  const city = params.get("city") || "all";
  const priceType = params.get("price_type") || "all";

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "all") next.delete(key);
    else next.set(key, val);
    setParams(next);
    if (key === "city") localStorage.setItem("ppc_city", !val || val === "all" ? "" : val);
  };

  useEffect(() => {
    if (!params.get("city")) {
      const saved = readCity();
      if (saved) {
        const next = new URLSearchParams(params);
        next.set("city", saved);
        setParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (category !== "all") query.category = category;
    if (country !== "all") query.country = country;
    if (city !== "all") query.city = city;
    if (priceType !== "all") query.price_type = priceType;
    if (q) query.q = q;
    api
      .get("/listings", { params: query })
      .then(({ data }) => setListings(data))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [category, country, city, priceType, q]);

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-10 md:pb-10 lg:px-8">
      <h1 className="font-display text-4xl font-medium tracking-tighter text-ppc-text lg:text-5xl">
        {t("browse_title")}
      </h1>

      {/* Mobile filters */}
      <div className="mt-5 md:hidden" data-testid="browse-filters-mobile">
        <div className="flex items-center justify-between gap-3">
          <LocationPicker value={city === "all" ? "" : city} onChange={(v) => setParam("city", v || "all")} />
          {(country !== "all" || city !== "all" || priceType !== "all") && (
            <button
              type="button"
              data-testid="clear-filters-btn"
              onClick={() => setParams(new URLSearchParams(q ? { q } : {}))}
              className="chip"
            >
              {t("filter_all")}
            </button>
          )}
        </div>

        <label className="search-inset mt-4 flex h-12 items-center gap-2.5 rounded-2xl px-4">
          <MagnifyingGlass size={18} color="#9184d9" />
          <input
            data-testid="browse-search-input-mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("browse_search")}
            className="min-w-0 flex-1 bg-transparent text-[14px] text-ppc-text outline-none placeholder:text-ppc-muted"
          />
        </label>

        <div className="mt-3 grid grid-cols-2 gap-2.5">
          <Select value={country} onValueChange={(v) => setParam("country", v)}>
            <SelectTrigger data-testid="filter-country-mobile" className="h-11 rounded-2xl border-ppc bg-ppc-bg text-[13px]">
              <SelectValue placeholder={t("filter_country")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filter_country")} · {t("filter_all")}</SelectItem>
              {EUROPE_COUNTRIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={city} onValueChange={(v) => setParam("city", v)}>
            <SelectTrigger data-testid="filter-city-mobile" className="h-11 rounded-2xl border-ppc bg-ppc-bg text-[13px]">
              <SelectValue placeholder={t("filter_city")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filter_city")} · {t("filter_all")}</SelectItem>
              {country !== "all"
                ? citiesFor(country).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)
                : CITY_GROUPS.map((g) => (
                    <SelectGroup key={g.country}>
                      <SelectLabel className="text-ppc-muted">{g.country}</SelectLabel>
                      {g.cities.map((c) => (
                        <SelectItem key={`${g.country}-${c}`} value={c}>{c}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-2.5 flex gap-2">
          {[
            ["all", t("filter_all")],
            ["hourly", t("price_hourly")],
            ["fixed", t("price_fixed")],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              data-testid={`filter-pricetype-${val}`}
              onClick={() => setParam("price_type", val)}
              className={`chip ${priceType === val ? "chip-active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop filters */}
      <div className="card-focal mt-6 hidden flex-col gap-3 p-4 md:flex lg:flex-row lg:items-center">
        <label className="search-inset flex h-12 flex-1 items-center gap-3 rounded-2xl px-4">
          <MagnifyingGlass size={18} color="#9184d9" />
          <input
            data-testid="browse-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("browse_search")}
            className="flex-1 bg-transparent text-sm text-ppc-text outline-none placeholder:text-ppc-muted"
          />
        </label>

        <Select value={country} onValueChange={(v) => setParam("country", v)}>
          <SelectTrigger data-testid="filter-country" className="h-12 rounded-2xl border-ppc bg-ppc-bg lg:w-44">
            <SelectValue placeholder={t("filter_country")} />
          </SelectTrigger>
          <SelectContent className="">
            <SelectItem value="all">{t("filter_country")} · {t("filter_all")}</SelectItem>
            {EUROPE_COUNTRIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={city} onValueChange={(v) => setParam("city", v)}>
          <SelectTrigger data-testid="filter-city" className="h-12 rounded-2xl border-ppc bg-ppc-bg lg:w-44">
            <SelectValue placeholder={t("filter_city")} />
          </SelectTrigger>
          <SelectContent className="">
            <SelectItem value="all">{t("filter_city")} · {t("filter_all")}</SelectItem>
            {country !== "all"
              ? citiesFor(country).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)
              : CITY_GROUPS.map((g) => (
                  <SelectGroup key={g.country}>
                    <SelectLabel className="text-ppc-muted">{g.country}</SelectLabel>
                    {g.cities.map((c) => (
                      <SelectItem key={`${g.country}-${c}`} value={c}>{c}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
          </SelectContent>
        </Select>

        <Select value={priceType} onValueChange={(v) => setParam("price_type", v)}>
          <SelectTrigger data-testid="filter-pricetype" className="h-12 rounded-2xl border-ppc bg-ppc-bg lg:w-44">
            <SelectValue placeholder={t("filter_pricetype")} />
          </SelectTrigger>
          <SelectContent className="">
            <SelectItem value="all">{t("filter_pricetype")} · {t("filter_all")}</SelectItem>
            <SelectItem value="hourly">{t("price_hourly")}</SelectItem>
            <SelectItem value="fixed">{t("price_fixed")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="no-scrollbar mt-4 flex gap-2.5 overflow-x-auto pb-2 md:flex-wrap md:overflow-x-visible" data-testid="filter-category">
        <button
          type="button"
          data-testid="filter-category-all"
          onClick={() => setParam("category", "all")}
          className={`cat-tile grid place-items-center ${category === "all" ? "cat-tile-active" : ""}`}
        >
          <span className="text-[12.5px] text-ppc-text">{t("filter_all")}</span>
        </button>
        {ALL_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            data-testid={`filter-category-${c}`}
            onClick={() => setParam("category", c)}
            className={`cat-tile ${category === c ? "cat-tile-active" : ""}`}
          >
            <img src={CATEGORY_META[c].image} alt="" loading="lazy" />
            <span className="cat-tile-label">{tc(c)}</span>
          </button>
        ))}
      </div>

      <VerifiedSlider city={city} country={country} category={category} />

      {/* Results */}
      {loading ? (        <div className="mt-6 grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton h-[92px]" />)}</div>
      ) : listings.length === 0 ? (
        <p className="mt-16 text-center text-ppc-muted" data-testid="browse-no-results">
          {t("no_results")}
        </p>
      ) : (
        <>
          <p className="mt-6 text-sm text-ppc-muted">
            {listings.length} {t("results_count")}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 md:hidden" data-testid="browse-results-mobile">
            {listings.map((l) => (
              <ServiceCard key={l.id} listing={l} />
            ))}
          </div>
          <div className="mt-3 hidden gap-2.5 md:grid md:grid-cols-2 xl:grid-cols-3">
            {listings.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
